import React from 'react';
import imageDimensions from '../image-dimensions.json';

// Drop-in replacement for <img> on /assets raster images.
//
// - Renders a <picture> with an auto-generated WebP source (see
//   optimize-images.mjs) and the original file as the fallback. The <picture>
//   uses `display: contents`, so it generates no box of its own — layout is
//   identical to a bare <img> (safe because no CSS here uses `> img`).
// - Sets intrinsic width/height from the generated dimensions manifest so the
//   browser reserves the image's space and avoids layout shift (CLS). Browsers
//   derive aspect-ratio from these attributes and keep the image responsive
//   under CSS `width:100%` — without overriding any explicit CSS sizing.
// - Non-raster sources (svg, data URIs, external URLs without a png/jpg
//   extension) fall through to a plain <img>, so it is safe to use everywhere.
type ImgProps = React.ImgHTMLAttributes<HTMLImageElement> & { src: string };

const RASTER = /\.(png|jpe?g)$/i;
const DIMS = imageDimensions as Record<string, { w: number; h: number }>;

export default function Img({ src, width, height, ...rest }: ImgProps) {
  const dim = DIMS[src];
  const w = width ?? dim?.w;
  const h = height ?? dim?.h;

  if (!RASTER.test(src)) return <img src={src} width={w} height={h} {...rest} />;
  const webp = src.replace(RASTER, '.webp');
  return (
    <picture style={{ display: 'contents' }}>
      <source srcSet={encodeURI(webp)} type="image/webp" />
      <img src={src} width={w} height={h} {...rest} />
    </picture>
  );
}
